import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { partnerPortalApi } from '../../api/partnerPortal';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';
import { DataTable } from '../../components/DataTable';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ProjectDocuments({ projectId, canSubmit }: { projectId: number; canSubmit: boolean }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const documentsQuery = useQuery({ queryKey: ['partner-project-documents', projectId], queryFn: () => partnerPortalApi.projectDocuments(projectId) });
  const { register, handleSubmit, reset, setValue } = useForm<{ documentType: string; file: FileList }>();

  const uploadMutation = useMutation({
    mutationFn: async (values: { documentType: string; file: FileList }) => {
      const file = values.file[0];
      const fileDataUri = await fileToDataUri(file);
      return partnerPortalApi.uploadProjectDocument(projectId, { fileDataUri, originalFilename: file.name, documentType: values.documentType });
    },
    onSuccess: () => { showSuccess('Document submitted.'); queryClient.invalidateQueries({ queryKey: ['partner-project-documents', projectId] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit document.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {documentsQuery.data && (
          <DataTable
            rows={documentsQuery.data.data}
            rowKey={(d) => d.id}
            emptyMessage="No documents on this project yet."
            columns={[
              { header: 'File', render: (d) => <a href={partnerPortalApi.downloadUrl(d.id)}>{d.original_filename}</a> },
              { header: 'Type', render: (d) => d.document_type },
              { header: 'Size', render: (d) => formatBytes(d.size_bytes) },
              { header: 'Uploaded By', render: (d) => d.uploaded_by_partner_organization ? `${d.uploaded_by_name} (${d.uploaded_by_partner_organization})` : d.uploaded_by_name ?? '-' },
              { header: 'Date', render: (d) => d.created_at },
            ]}
          />
        )}

        {canSubmit && (
          <form className="inline-form" onSubmit={handleSubmit((values) => uploadMutation.mutate(values))}>
            <label>Document Type<input {...register('documentType', { required: true })} placeholder="e.g. progress_report" /></label>
            <label>File<input type="file" onChange={(e) => setValue('file', e.target.files as FileList)} /></label>
            <button type="submit" disabled={uploadMutation.isPending}>{uploadMutation.isPending ? 'Submitting…' : 'Submit Document'}</button>
          </form>
        )}
        {!canSubmit && <p className="muted">Your account type is view-only for this project - submissions come from the Implementing Partner on record.</p>}
      </div>
    </div>
  );
}

/**
 * The real, deliberately-deferred access from migration 046, built
 * out per explicit direction. Capability is fixed by partner_type:
 * Donor and Auditor see their linked projects and documents,
 * read-only; Implementing Partner can also submit new documents.
 * Visibility itself is never blanket - only projects with a real
 * partner_project_links row ever appear here.
 */
export function PartnerPortalShellPage() {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const meQuery = useQuery({ queryKey: ['partner-me'], queryFn: () => partnerPortalApi.me() });
  const projectsQuery = useQuery({ queryKey: ['partner-my-projects'], queryFn: () => partnerPortalApi.myProjects(), enabled: !!meQuery.data?.data });

  if (meQuery.isLoading) return null;
  if (!meQuery.data?.data) return <Navigate to="/login" replace />;

  const account = meQuery.data.data;
  const selectedProject = projectsQuery.data?.data.find((p) => p.id === selectedProjectId);
  const canSubmit = account.partnerType === 'implementing_partner';

  return (
    <div className="page">
      <div className="page-header">
        <h1>Welcome, {account.fullName}</h1>
        <p className="muted">{account.organizationName}</p>
      </div>

      {!selectedProjectId && (
        <div className="panel">
          <div className="panel-body">
            {projectsQuery.data && projectsQuery.data.data.length === 0 && (
              <p className="muted">Your account isn't linked to any projects yet. Contact Verdantrise if you were expecting to see one here.</p>
            )}
            {projectsQuery.data && projectsQuery.data.data.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--spacing-sm, 12px)' }}>
                {projectsQuery.data.data.map((p) => (
                  <button key={p.id} className="card" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setSelectedProjectId(p.id)}>
                    <h3>{p.project_name}</h3>
                    <p className="muted">{p.project_reference}</p>
                    <p className="muted">{p.status}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedProjectId && selectedProject && (
        <>
          <button className="link-button" onClick={() => setSelectedProjectId(null)}>&larr; Back to projects</button>
          <div className="page-header"><h2>{selectedProject.project_name}</h2></div>
          <ProjectDocuments projectId={selectedProjectId} canSubmit={canSubmit} />
        </>
      )}
    </div>
  );
}
